import { PresignedUploadError } from "./imageUploadError.error";

type PresignedPostData = {
  url: string;
  fields: Record<string, string>;
};

const parseTag = (xml: string, tag: string) =>
  new RegExp(`<${tag}>([^<]+)</${tag}>`).exec(xml)?.[1];

const parseNumericTag = (xml: string, tag: string) => {
  const raw = parseTag(xml, tag);
  return raw === undefined ? undefined : Number(raw);
};

/** S3 reports POST failures as an XML error document rather than JSON. */
const parseS3Error = (xml: string) => ({
  s3Code: parseTag(xml, "Code"),
  s3Message: parseTag(xml, "Message"),
  // Only present on EntityTooLarge, but when they are they beat guessing.
  maxSizeAllowed: parseNumericTag(xml, "MaxSizeAllowed"),
  proposedSize: parseNumericTag(xml, "ProposedSize"),
});

export async function uploadToPresignedUrl(
  presigned: PresignedPostData,
  blob: Blob,
  contentType = "image/jpeg",
): Promise<void> {
  const formData = new FormData();
  Object.entries(presigned.fields).forEach(([k, v]) => formData.append(k, v));
  formData.append("Content-Type", contentType);
  formData.append("file", blob, "upload");

  let res: Response;
  try {
    res = await fetch(presigned.url, { method: "POST", body: formData });
  } catch (e) {
    // fetch only rejects when the request never completed — offline, DNS/CORS
    // failure, or a browser extension blocking the S3 host. No status to report.
    throw new PresignedUploadError(
      `S3 upload could not be reached: ${e instanceof Error ? e.message : String(e)}`,
    );
  }

  if (!res.ok) {
    const details = parseS3Error(await res.text().catch(() => ""));
    const { s3Code, s3Message, maxSizeAllowed, proposedSize } = details;
    throw new PresignedUploadError(
      [
        `S3 upload failed: ${res.status}`,
        s3Code && `[${s3Code}]`,
        s3Message && `- ${s3Message}`,
        proposedSize !== undefined &&
          maxSizeAllowed !== undefined &&
          `(sent ${proposedSize} bytes, cap ${maxSizeAllowed})`,
      ]
        .filter(Boolean)
        .join(" "),
      { status: res.status, ...details },
    );
  }
}
